import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { workflowsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { EvalPanel } from "@/components/evals/eval-panel";
import { WorkflowTabs } from "@/components/workflows/workflow-tabs";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

/**
 * Evaluation for a workflow, or for one run of it, on its own page.
 *
 * It used to sit above the runs table, where a report with six findings and a
 * coverage block pushed the table clean off the screen — you had to scroll
 * past a page of prose to reach the thing the page is named after. An
 * evaluation is something you read, so it gets somewhere to be read.
 *
 * Constrained to a readable measure rather than filling an ultrawide monitor:
 * this is body text, and full-width body text is punishing to scan.
 */
export function WorkflowEvaluation() {
  const { workflowId, runId } = useParams<{ workflowId: string; runId?: string }>();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: workflow } = useQuery({
    queryKey: queryKeys.workflows.detail(workflowId ?? ""),
    queryFn: () => workflowsApi.get(workflowId!),
    enabled: !!workflowId,
  });

  const scopedToRun = !!runId;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <button
          onClick={() =>
            navigate(
              withWorkspaceContext(
                scopedToRun
                  ? `/tenants/${tenantId}/workflows/${workflowId}/runs/${runId}`
                  : `/tenants/${tenantId}/workflows/${workflowId}`,
                searchParams,
              ),
            )
          }
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label={scopedToRun ? "Back to run" : "Back to canvas"}
        >
          <ArrowLeft size={14} />
        </button>
        <div className="flex flex-1 items-center gap-2 text-sm">
          <Sparkles size={14} className="text-text-muted" />
          <span className="font-semibold text-text-primary">
            {workflow?.name ?? "Workflow"}
          </span>
          <span className="text-text-muted">
            / {scopedToRun ? `run ${runId!.slice(0, 8)} evaluation` : "evaluation"}
          </span>
        </div>
        {/* A run-scoped evaluation is reached from the run, not from the
            workflow tabs, so the switcher would point somewhere unexpected. */}
        {!scopedToRun && tenantId && workflowId && (
          <WorkflowTabs tenantId={tenantId} workflowId={workflowId} active="evaluation" />
        )}
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-5">
          {scopedToRun ? (
            <EvalPanel targetType="workflow_run" targetId={runId!} />
          ) : (
            <EvalPanel targetType="workflow" targetId={workflowId!} />
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-text-muted">
            {scopedToRun
              ? "Scored against this single run: its steps, retries, timings and cost."
              : "Scored across this workflow's recent runs. For one specific run, open it from the Runs tab and evaluate from there."}
          </p>
        </div>
      </div>
    </div>
  );
}
